<template>
  <div>
    <div v-if="showKidTabs" class="kid-tabs">
      <button
        v-for="(kid, index) in kidsForTabs"
        :key="kid.kidId || kid.slotKey || index"
        type="button"
        class="kid-tab"
        :class="{ active: kid.kidId === activeKidId }"
        @click="switchKid(kid.kidId)"
      >
        {{ kidTabLabel(kid, index) }}
      </button>
    </div>

    <transition :name="animationClass">
      <h1
        v-if="canShowGreeting && greetingName && isVisible"
        class="header"
      >
        Hi, {{ greetingName }} 👋🏽
      </h1>
    </transition>

    <transition-group
      name="fade-scale"
      tag="div"
      class="level-container"
      appear
    >
      <LevelCard
        v-for="(category, index) in categories"
        :key="category.name"
        :title="category.title"
        :image="category.image"
        :locked="!isLevelUnlocked(index)"
        @card-click="handleCategoryClick(category, index)"
      />
    </transition-group>

    <transition name="fade-scale">
      <div
        v-if="showPaymentModal"
        class="modal-overlay"
        @click.self="closePaymentModal"
      >
        <div class="modal-content">
          <h2>Desbloquea Todos los Niveles por 1 Año</h2>
          <p>
            Para acceder a todos los niveles, por favor realiza un pago de
            <strong>$100,000 COP</strong> haciendo click
            <a href="https://checkout.wompi.co/l/4r0WxD">aquí</a>, o vía Nequi
            al número <strong>313 741 2024</strong>. Envía la confirmación de
            pago a este WhatsApp: <strong>+57 313 741 2024</strong>.
          </p>
          <p>
            La activación puede tardar un par de horas, si ya pagaste y enviaste
            la confirmación en WhatsApp, solo espera un momento,
            <strong>Pronto activaremos tu cuenta.</strong>
          </p>
          <button @click="closePaymentModal">
            Cerrar
          </button>
        </div>
      </div>
    </transition>

    <footer>
      <p>
        ¿Necesitas Ayuda? Escríbenos en WhatsApp al 313 741 2024, o envíanos un
        correo a Vocablly.play@Gmail.com
      </p>
      <p>©2025 Vocablly</p>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import LevelCard from "../components/cards/LevelCard.vue";
import { categories } from "../utils/levelData.js";
import useEnterAnimation from "../composables/useEnterAnimation.js";
import { getTeacherCodeAccess } from "../utils/teacherCodeAccess.js";

export default {
  name: "HomePage",
  components: {
    LevelCard,
  },
  setup() {
    const { isVisible, animationClass } = useEnterAnimation(100, "fade-scale");
    return { isVisible, animationClass };
  },
  data() {
    return {
      categories,
      showPaymentModal: false,
      teacherCodeAccess: null,
    };
  },
  computed: {
    ...mapState("auth", ["authReady"]),
    ...mapGetters("auth", ["loggedInUser", "userData"]),

    hasTeacherCodeAccess() {
      return !!this.teacherCodeAccess;
    },

    loggedIn() {
      return !!this.loggedInUser;
    },

    hasAppAccess() {
      return this.loggedIn || this.hasTeacherCodeAccess;
    },

    canShowGreeting() {
      return this.hasTeacherCodeAccess || this.authReady;
    },

    kidsForTabs() {
      return Array.isArray(this.userData?.kids) ? this.userData.kids : [];
    },

    showKidTabs() {
      return this.loggedIn && this.kidsForTabs.length > 1;
    },

    activeKidId() {
      return this.userData?.activeKidId || "";
    },

    greetingName() {
      if (this.hasTeacherCodeAccess) {
        return "Friend";
      }

      if (!this.authReady) return "";

      const role = String(this.userData?.role || "").toLowerCase();

      const kidFirstName = this.getFirstWord(this.userData?.kidName);
      const teacherFirstName = this.getFirstWord(this.userData?.teacherName);
      const displayFirstName = this.getFirstWord(this.loggedInUser?.displayName);

      const email =
        typeof this.loggedInUser?.email === "string"
          ? this.loggedInUser.email.trim()
          : "";

      if (role === "teacher" && teacherFirstName) return teacherFirstName;
      if (kidFirstName) return kidFirstName;

      return displayFirstName || email || "Friend";
    },
  },
  mounted() {
    this.syncTeacherCodeAccess();
    window.addEventListener("storage", this.syncTeacherCodeAccess);
  },
  beforeUnmount() {
    window.removeEventListener("storage", this.syncTeacherCodeAccess);
  },
  watch: {
    $route() {
      this.syncTeacherCodeAccess();
    },
  },
  methods: {
    getFirstWord(value) {
      if (typeof value !== "string") return "";
      const trimmed = value.trim();
      if (!trimmed) return "";
      return trimmed.split(/\s+/)[0];
    },

    kidTabLabel(kid, index) {
      return this.getFirstWord(kid?.kidName) || `Kid ${index + 1}`;
    },

    syncTeacherCodeAccess() {
      this.teacherCodeAccess = getTeacherCodeAccess();
    },

    pad2(value) {
      return String(value).padStart(2, "0");
    },

    formatDateToYMD(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return "";
      }

      return `${date.getFullYear()}-${this.pad2(
        date.getMonth() + 1
      )}-${this.pad2(date.getDate())}`;
    },

    getTodayYMD() {
      return this.formatDateToYMD(new Date());
    },

    isActiveUntilCurrentOrFuture(activeUntilValue) {
      if (!activeUntilValue) return false;

      if (typeof activeUntilValue === "string") {
        const trimmed = activeUntilValue.trim();
        if (!trimmed) return false;

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          return trimmed >= this.getTodayYMD();
        }

        const parsed = new Date(trimmed);
        const normalized = this.formatDateToYMD(parsed);
        return normalized ? normalized >= this.getTodayYMD() : false;
      }

      if (activeUntilValue instanceof Date) {
        const normalized = this.formatDateToYMD(activeUntilValue);
        return normalized ? normalized >= this.getTodayYMD() : false;
      }

      return false;
    },

    getEffectiveActiveUntil() {
      const activeKidActiveUntil = this.userData?.activeKid?.activeUntil || "";
      const accountActiveUntil =
        this.userData?.accountActiveUntil || this.userData?.activeUntil || "";

      return activeKidActiveUntil || accountActiveUntil;
    },

    async switchKid(kidId) {
      if (!kidId || kidId === this.activeKidId) return;

      this.showPaymentModal = false;

      try {
        await this.$store.dispatch("auth/selectActiveKid", kidId);
      } catch (error) {
        console.error("[HomePage] Failed switching kid:", error);
      }
    },

    isLevelUnlocked(index) {
      if (this.hasTeacherCodeAccess) {
        return true;
      }

      if (index < 2) return true;
      if (!this.loggedIn) return false;

      return this.isActiveUntilCurrentOrFuture(this.getEffectiveActiveUntil());
    },

    handleCategoryClick(category, index) {
      if (this.isLevelUnlocked(index)) {
        this.$router.push(`/${category.name}`);
        return;
      }

      if (!this.hasAppAccess) {
        this.$router.push("/login");
        return;
      }

      this.showPaymentModal = true;
    },

    closePaymentModal() {
      this.showPaymentModal = false;
    },
  },
};
</script>

<style scoped>
.kid-tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  padding: 20px 20px 0;
}

.kid-tab {
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: white;
  color: #222;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 700;
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.kid-tab:hover {
  transform: translateY(-1px);
}

.kid-tab.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.level-container {
  height: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  padding: 40px;
  max-width: 1000px;
  margin: auto;
}

@media (max-width: 600px) {
  .level-container {
    padding: 20px;
  }

  .kid-tabs {
    padding: 16px 16px 0;
    gap: 8px;
  }

  .kid-tab {
    padding: 8px 12px;
    font-size: 14px;
  }
}

.header {
  font-size: 48px;
  text-align: center;
  margin-top: 20px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 500px;
  text-align: center;
}

.modal-content h2 {
  margin-bottom: 20px;
}

.modal-content p {
  margin-bottom: 30px;
  line-height: 1.5;
}

.modal-content button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
}

.modal-content button:hover {
  background-color: #0056b3;
}

footer {
  padding: 20px;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  color: #000;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 2s ease, transform 2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: opacity 2s ease, transform 2s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-scale-enter-to,
.fade-scale-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>